import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import KanbanBoard from '../components/KanbanBoard';
import LeadDrawer from '../components/LeadDrawer';
import { useLeads } from '../hooks/useLeads';
import { createLead, bulkCreateLeads } from '../lib/leads';
import { parseLeadsWorkbook } from '../lib/importExcel';
import { SAMPLE_LEADS } from '../lib/sampleData';
import { STAGES, PRODUCTS, SOURCES } from '../lib/stages';

export default function Pipeline() {
  const { leads, loading, error } = useLeads();
  const [openLead, setOpenLead] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);

  async function onPickFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportBusy(true);
    try {
      const { leads: rows, issues } = await parseLeadsWorkbook(file);
      if (rows.length === 0) {
        setImportResult({ ok: false, msg: 'No valid rows found.', issues });
      } else if (confirm(`Import ${rows.length} leads from ${file.name}?`)) {
        const { created, skipped, dedupSkipped, writeError } = await bulkCreateLeads(rows, { skipDuplicates: true });
        if (writeError?.code === 'permission-denied') {
          setImportResult({
            ok: false,
            msg: 'Import blocked by Firestore rules. Run: firebase deploy --only firestore:rules — see the red banner at the top of the page.',
            issues
          });
        } else {
          const parts = [`Imported ${created.length} leads.`];
          if (skipped.length > 0) parts.push(`Skipped ${skipped.length} duplicate(s)`);
          if (dedupSkipped) parts.push('Dedup not run (rules issue)');
          setImportResult({ ok: created.length > 0, msg: parts.join('  ·  '), issues });
        }
      } else {
        setImportResult(null);
      }
    } catch (err) {
      setImportResult({ ok: false, msg: err.message });
    } finally {
      setImportBusy(false);
      e.target.value = '';
    }
  }

  async function seedSampleData() {
    if (!confirm('Add 10 sample Kenyan IT-services leads to your pipeline?')) return;
    await bulkCreateLeads(SAMPLE_LEADS);
  }

  return (
    <div className="max-w-[1400px] mx-auto p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h1 className="text-2xl font-semibold">Pipeline</h1>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={seedSampleData} className="btn-ghost border border-slate-200">+ Sample data</button>
          <Link to="/import/apollo" className="btn-ghost border border-slate-200">
            ⬆ Apollo import
          </Link>
          <button onClick={() => fileRef.current?.click()} className="btn-ghost border border-slate-200">
            {importBusy ? 'Importing…' : '⬆ Generic Excel'}
          </button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={onPickFile} className="hidden" />
          <button onClick={() => setShowNew(true)} className="btn-primary">+ New lead</button>
        </div>
      </div>

      {importResult && (
        <div className={`mb-3 p-3 rounded-lg text-sm ${importResult.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`}>
          <div className="font-medium">{importResult.msg}</div>
          {importResult.issues?.length > 0 && (
            <ul className="mt-1 list-disc pl-5">
              {importResult.issues.slice(0, 5).map((i, k) => <li key={k}>{i}</li>)}
              {importResult.issues.length > 5 && <li>…and {importResult.issues.length - 5} more.</li>}
            </ul>
          )}
        </div>
      )}

      {loading && (
        <div className="text-slate-500">Loading leads…</div>
      )}

      {!loading && error?.code === 'permission-denied' && (
        <div className="card p-6 border-red-200 bg-red-50 text-red-900">
          <div className="font-semibold mb-1">Pipeline can't load — Firestore rules not deployed</div>
          <div className="text-sm">
            Your account is signed in but Firestore is denying reads. See the red banner at the top of the page for the one-line fix
            (<span className="font-mono">firebase deploy --only firestore:rules</span> from <span className="font-mono">C:\APP</span>).
          </div>
        </div>
      )}

      {!loading && error && error?.code !== 'permission-denied' && (
        <div className="card p-6 border-amber-200 bg-amber-50 text-amber-900">
          <div className="font-semibold mb-1">Couldn't reach the database</div>
          <div className="text-sm font-mono">{error.code || error.message}</div>
        </div>
      )}

      {!loading && !error && (
        <KanbanBoard leads={leads} onOpenLead={setOpenLead} />
      )}

      {openLead && <LeadDrawer lead={leads.find(l => l.id === openLead.id) || openLead} onClose={() => setOpenLead(null)} />}
      {showNew && <NewLeadModal onClose={() => setShowNew(false)} />}
    </div>
  );
}

function NewLeadModal({ onClose }) {
  const [form, setForm] = useState({
    companyName: '', industry: '', contactName: '', contactTitle: '', phone: '', email: '',
    stage: 'Prospect', productInterest: 'Managed IT', leadSource: 'LinkedIn Personal',
    estimatedMonthlyValueKES: 0, nextAction: '', notes: ''
  });
  const [saving, setSaving] = useState(false);
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await createLead({ ...form, estimatedMonthlyValueKES: Number(form.estimatedMonthlyValueKES || 0), dateAdded: new Date() });
      onClose();
    } finally { setSaving(false); }
  }
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 p-4">
      <form onSubmit={save} className="card w-full max-w-lg p-5 space-y-3">
        <div className="text-lg font-semibold">New lead</div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><label className="label">Company *</label><input className="input" required value={form.companyName} onChange={e=>set('companyName', e.target.value)} /></div>
          <div><label className="label">Industry</label><input className="input" value={form.industry} onChange={e=>set('industry', e.target.value)} /></div>
          <div><label className="label">Contact name</label><input className="input" value={form.contactName} onChange={e=>set('contactName', e.target.value)} /></div>
          <div className="col-span-2"><label className="label">Contact title</label><input className="input" value={form.contactTitle} onChange={e=>set('contactTitle', e.target.value)} placeholder="e.g. CFO, IT Manager, Operations Director" /></div>
          <div><label className="label">Phone</label><input className="input" value={form.phone} onChange={e=>set('phone', e.target.value)} placeholder="+254…" /></div>
          <div><label className="label">Email</label><input className="input" type="email" value={form.email} onChange={e=>set('email', e.target.value)} /></div>
          <div><label className="label">Stage</label><select className="input" value={form.stage} onChange={e=>set('stage', e.target.value)}>{STAGES.map(s=> <option key={s}>{s}</option>)}</select></div>
          <div><label className="label">Product interest</label><select className="input" value={form.productInterest} onChange={e=>set('productInterest', e.target.value)}>{PRODUCTS.map(s=> <option key={s}>{s}</option>)}</select></div>
          <div><label className="label">Lead source</label><select className="input" value={form.leadSource} onChange={e=>set('leadSource', e.target.value)}>{SOURCES.map(s=> <option key={s}>{s}</option>)}</select></div>
          <div><label className="label">Est. KES / month</label><input className="input" type="number" min="0" value={form.estimatedMonthlyValueKES} onChange={e=>set('estimatedMonthlyValueKES', e.target.value)} /></div>
          <div className="col-span-2"><label className="label">Next action</label><input className="input" value={form.nextAction} onChange={e=>set('nextAction', e.target.value)} /></div>
          <div className="col-span-2"><label className="label">Notes</label><textarea className="input min-h-[80px]" value={form.notes} onChange={e=>set('notes', e.target.value)} /></div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <button disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Create lead'}</button>
        </div>
      </form>
    </div>
  );
}
